import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Input, Tag, Drawer, notification } from "antd";
import { TagOutlined, CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getVouchersByOrder, checkVoucher } from "../services/topupService";

const formatVND = (amount) => {
  return new Intl.NumberFormat("vi-VN", {
    style: "currency",
    currency: "VND",
  }).format(amount || 0);
};

const VoucherSection = ({
  total = 0,
  quantity = 1,
  disabled = false,
  resetKey = null,
  serviceImage = "/images/payment_card.png",
  className = "",
  onChange,
}) => {
  const [openVoucher, setOpenVoucher] = useState(false);
  const [vouchers, setVouchers] = useState([]);
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherValue, setVoucherValue] = useState(0);
  const [voucherPercent, setVoucherPercent] = useState(null);
  const [manualVoucherCode, setManualVoucherCode] = useState("");
  const [voucherTxt, setVoucherTxt] = useState("");
  const [loading, setLoading] = useState(false);

  const prevProps = useRef({ total, quantity, resetKey });
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const handleClearVoucher = useCallback(() => {
    setVoucherCode("");
    setVoucherValue(0);
    setVoucherPercent(null);
    setVoucherTxt("");
    setManualVoucherCode("");
    onChangeRef.current?.({ code: "", value: 0, percent: null });
  }, []);

  // Chỉ reset voucher khi total, quantity hoặc resetKey THỰC SỰ thay đổi giá trị
  useEffect(() => {
    const hasTotalChanged = prevProps.current.total !== total;
    const hasQuantityChanged = prevProps.current.quantity !== quantity;
    const hasResetKeyChanged = prevProps.current.resetKey !== resetKey;

    if (hasTotalChanged || hasQuantityChanged || hasResetKeyChanged) {
      prevProps.current = { total, quantity, resetKey };
      handleClearVoucher();
    }
  }, [total, quantity, resetKey, handleClearVoucher]);

  const getVouchers = async () => {
    if (!total || disabled) return;
    setLoading(true);
    try {
      const res = await getVouchersByOrder({
        Quantity: quantity || 1,
        Total: total,
      });
      setVouchers(res?.data?.data || []);
    } catch (error) {
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  };

  const applyVoucherPick = (voucher) => {
    const currentTotal = total || 0;
    let amount = Number(
      voucher.amountvoucher ||
      voucher.amount ||
      voucher.discountvalue ||
      voucher.discountValue ||
      0
    );
    const discountType = (voucher.discounttype || voucher.discountType || "").toUpperCase();
    const discountVal = Number(voucher.discountvalue || voucher.discountValue || 0);
    const maxDiscount = Number(voucher.maxdiscountvalue || voucher.maxDiscountValue || 0);

    if (discountType === "PERCENTAGE") {
      const amountDiscount = Math.ceil(currentTotal * (discountVal / 100));
      amount = maxDiscount > 0 ? Math.min(amountDiscount, maxDiscount) : amountDiscount;
    }

    const finalAmount = Math.min(amount, currentTotal);
    const percent = discountType === "PERCENTAGE" && discountVal ? discountVal : null;

    setVoucherValue(finalAmount);
    setVoucherCode(voucher.code);
    setVoucherPercent(percent);
    setVoucherTxt(
      `<div class="text-[14px]">Áp dụng mã giảm giá thành công! <span class="text-green-600 font-bold text-[15px]">-${finalAmount.toLocaleString("vi-VN")} đ</span></div>`
    );
    setOpenVoucher(false);

    onChangeRef.current?.({ code: voucher.code, value: finalAmount, percent });
  };

  const applyVoucher = async (e) => {
    const code = (typeof e === "string" ? e : manualVoucherCode || "").trim().toUpperCase();
    if (!code) {
      setVoucherTxt(`<div class="text-[14px] text-red-600">Vui lòng nhập mã giảm giá!</div>`);
      handleClearVoucher();
      return;
    }

    if (disabled || !total) {
      notification.warning({
        message: "Thông báo",
        description: "Vui lòng chọn sản phẩm trước khi áp dụng mã!",
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        Quantity: quantity || 1,
        Total: total,
        VoucherCode: code,
        FlightRouteType: null,
      };

      const res = await checkVoucher(payload);
      const voucherData = res?.data?.data || res?.data;

      const isSuccess =
        (res?.data?.statuscode === 200 || res?.status === 200);

      if (isSuccess && voucherData) {
        applyVoucherPick(voucherData);
        notification.success({
          message: "Thành công",
          description: `Áp dụng mã giảm giá ${code} thành công!`,
        });
      } else {
        const msg =
          voucherData?.message ||
          res?.data?.message ||
          "Mã giảm giá không hợp lệ hoặc không đủ điều kiện!";
        setVoucherTxt(`<div class="text-[14px] text-red-600">${msg}</div>`);
        notification.error({
          message: "Thất bại",
          description: msg,
        });
        handleClearVoucher();
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.err ||
        "Mã giảm giá không hợp lệ hoặc đã hết hạn!";
      setVoucherTxt(`<div class="text-[14px] text-red-600">${msg}</div>`);
      notification.error({
        message: "Thất bại",
        description: msg,
      });
      handleClearVoucher();
    } finally {
      setLoading(false);
    }
  };

  const isApplied = voucherCode !== "" && voucherValue > 0;

  return (
    <div className={className}>
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm font-semibold text-gray-700">
          Mã khuyến mãi
        </span>
        <div className="flex items-center gap-2">
          <Tag
            onClick={() => {
              setOpenVoucher(true);
              getVouchers();
            }}
            color={!isApplied ? "default" : "success"}
            icon={<TagOutlined />}
            className="cursor-pointer font-medium py-0.5 px-2.5 rounded-lg text-[13px]"
          >
            {!isApplied
              ? "Chọn mã giảm giá"
              : voucherPercent
              ? `${voucherCode} (-${voucherPercent}%)`
              : voucherCode}
          </Tag>
          {isApplied && (
            <CloseOutlined
              onClick={handleClearVoucher}
              style={{ fontSize: 16, color: "red", cursor: "pointer" }}
            />
          )}
        </div>
      </div>

      {voucherTxt && (
        <div
          className="mb-3 text-sm"
          dangerouslySetInnerHTML={{ __html: voucherTxt }}
        />
      )}

      <Drawer
        title={
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center">
              <span className="uppercase font-bold text-[18px]">
                Mã giảm giá của tôi
              </span>
              <CloseOutlined
                onClick={() => setOpenVoucher(false)}
                style={{ fontSize: 18, cursor: "pointer" }}
              />
            </div>
            <div>
              <Input.Search
                placeholder="Nhập mã giảm giá khác..."
                enterButton={
                  <Button
                    type="primary"
                    loading={loading}
                    className="bg-primary text-white font-medium"
                  >
                    Áp dụng
                  </Button>
                }
                allowClear
                value={manualVoucherCode}
                onChange={(e) => setManualVoucherCode(e.target.value.toUpperCase())}
                onSearch={applyVoucher}
              />
            </div>
          </div>
        }
        placement={"bottom"}
        closable={false}
        onClose={() => setOpenVoucher(false)}
        open={openVoucher}
        height={"100%"}
        bodyStyle={{
          background: "#f3f6f6",
        }}
      >
        <div>
          {vouchers.map((voucher) => {
            const discountType = (voucher.discounttype || voucher.discountType || "").toUpperCase();
            const discountVal = voucher.discountvalue || voucher.discountValue;
            const maxDiscount = voucher.maxdiscountvalue || voucher.maxDiscountValue;
            return (
              <div
                key={voucher.id || voucher.code}
                style={{
                  boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
                }}
                className="rounded-[14px] bg-white p-3 mb-4"
              >
                <div className="flex gap-4 w-full">
                  <div className="w-1/5 flex items-center justify-center">
                    <img
                      src={serviceImage}
                      className="w-[32px]"
                      alt=""
                    />
                  </div>
                  <div className="border-lime-300 border-1 flex flex-col gap-1 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-[16px] text-primary">
                        Mã {voucher.code}
                      </div>
                      {discountType === "PERCENTAGE" && discountVal && (
                        <Tag color="success" className="mr-0 font-semibold text-[11px]">
                          -{String(discountVal).replace("%", "")}%
                        </Tag>
                      )}
                    </div>
                    <div className="text-red-600 text-[14px] font-bold">
                      {voucher.title}
                    </div>
                    <div className="text-[14px]">
                      Giảm {discountType === "PERCENTAGE" ? `${discountVal}%` : formatVND(voucher.amountvoucher || voucher.amount)}
                      {maxDiscount && discountType === "PERCENTAGE" && (
                        <span className="text-gray-500"> (tối đa {formatVND(maxDiscount)})</span>
                      )}
                    </div>
                    {voucher.startdate && voucher.enddate && (
                      <div className="text-[12px] text-gray-500">
                        Áp dụng từ {dayjs(voucher.startdate).format("DD/MM/YYYY")} đến {dayjs(voucher.enddate).format("DD/MM/YYYY")}
                      </div>
                    )}
                    <div
                      onClick={() => applyVoucherPick(voucher)}
                      className="text-primary font-semibold text-[14px] ml-auto cursor-pointer"
                    >
                      Áp dụng
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {vouchers.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">
              Không có mã giảm giá nào
            </div>
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default VoucherSection;
